import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {EligibleOfferSetModel} from '../../../../../model/eligible-offer-set.model';
import {OfferRuleService} from '../../../../../service/offer-rule.service';
import {VehicleService} from '../../../../../service/vehicle.service';
import {MakeModel} from '../../../../../model/make.model';
import {VehicleModel} from '../../../../../model/vehicle-model.model';

@Component({
  selector: 'auto-offer-set-rule-modal',
  templateUrl: './offer-set-rule-modal.component.html',
  styleUrls: ['./offer-set-rule-modal.component.scss'],
  standalone: false
})
export class OfferSetRuleModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() dialogTitle = '';
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() rule: OfferRuleModel | null = null;
  @Input() accountId: number;
  @Input() selectedRuleType = 'offerSetRule';
  @Input() eligibleOfferSets: EligibleOfferSetModel[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  ruleForm: FormGroup;
  ruleTypeOptions = [
    {label: 'Offer Set Rule', value: 'offerSetRule'},
    {label: 'Dollar Discount Rule', value: 'discountRule'}
  ];
  conditionOptions = [
    {label: 'New', value: 'New'},
    {label: 'Used', value: 'Used'},
    {label: 'CPO', value: 'CPO'},
    {label: 'Any', value: 'Any'}
  ];

  makes: MakeModel[] = [];
  models: VehicleModel[] = [];
  filteredModels: VehicleModel[] = [];
  anyMakeOption: MakeModel;
  anyModelOption: VehicleModel;
  modelDropdownDisabled = true;
  savingRule = false;
  selectedRuleId: number | null = null;
  selectedMakeId: number | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private offerRuleService: OfferRuleService,
    private vehicleService: VehicleService
  ) {
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeAnyOptions();
    this.loadMakes();
    this.loadModels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const visibilityChange = changes['visible'];
    if (visibilityChange) {
      this.handleVisibilityChange();
    }
    const ruleChange = changes['rule'];
    if (ruleChange && this.visible) {
      this.applyRuleToForm();
    }
  }

  onRuleTypeChange(): void {
    const ruleTypeControl = this.ruleForm.get('ruleType');
    const offerSetControl = this.ruleForm.get('offerSetId');
    const discountControl = this.ruleForm.get('dollarAmountDiscount');
    let isDiscountRule = false;
    if (ruleTypeControl) {
      const ruleTypeValue = ruleTypeControl.value;
      isDiscountRule = ruleTypeValue === 'discountRule';
    }
    if (offerSetControl) {
      if (isDiscountRule) {
        offerSetControl.clearValidators();
        offerSetControl.setValue(null);
        offerSetControl.disable({emitEvent: false});
      } else {
        offerSetControl.setValidators([Validators.required]);
        offerSetControl.enable({emitEvent: false});
      }
      offerSetControl.updateValueAndValidity();
    }
    if (discountControl) {
      if (isDiscountRule) {
        discountControl.setValidators([Validators.required, Validators.min(0.01)]);
        discountControl.enable({emitEvent: false});
      } else {
        discountControl.setValidators([Validators.min(0.01)]);
        discountControl.setValue(null);
        discountControl.disable({emitEvent: false});
      }
      discountControl.updateValueAndValidity();
    }
  }

  onOfferSetChange(offerSetId: number): void {
    const matchingOfferSet = this.eligibleOfferSets.find((offerSet) => offerSet.id === offerSetId);
    const hasMatch = matchingOfferSet !== undefined;
    if (hasMatch && matchingOfferSet) {
      const offerSetPatch = this.buildOfferSetPatch(matchingOfferSet);
      this.ruleForm.patchValue(offerSetPatch);
      this.applyMakeAndModelSelectionFromForm();
    }
  }

  onMakeSelectionChange(makeId: number | null): void {
    this.filterModelsForMake(makeId);
    const makeName = this.getMakeNameById(makeId);
    this.ruleForm.patchValue({
      make: makeName,
      model: '',
      modelId: null
    });
  }

  onModelSelectionChange(modelId: number | null): void {
    let modelName = '';
    const hasValue = modelId !== null;
    if (hasValue) {
      const match = this.filteredModels.find((model) => model.id === modelId);
      if (match) {
        modelName = match.name;
      }
    }
    this.ruleForm.patchValue({
      model: modelName
    });
  }

  saveRule(): void {
    const formValid = this.isFormValidForSave();
    if (formValid) {
      const payload = this.buildPayloadFromForm();
      this.submitRule(payload);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please correct the highlighted fields before saving.'
      });
    }
  }

  closeDialog(): void {
    this.savingRule = false;
    this.closed.emit();
  }

  private handleVisibilityChange(): void {
    if (this.visible) {
      this.applyRuleToForm();
    }
    if (!this.visible) {
      this.resetState();
    }
  }

  private applyRuleToForm(): void {
    if (this.mode === 'edit' && this.rule) {
      this.setFormForEdit(this.rule);
    } else {
      this.setDefaultFormValues();
    }
    this.onRuleTypeChange();
  }

  private initializeForm(): void {
    this.ruleForm = this.formBuilder.group({
      ruleType: ['offerSetRule', Validators.required],
      status: [true, Validators.required],
      offerSetId: [null],
      year: [null],
      make: [''],
      makeId: [null],
      model: [''],
      modelId: [null],
      trim: [''],
      maxSalePrice: [null, Validators.min(0)],
      vehicleLimit: [null, Validators.min(0)],
      vehicleCondition: ['New', Validators.required],
      dollarAmountDiscount: [null, Validators.min(0.01)]
    });
  }

  private initializeAnyOptions(): void {
    this.anyMakeOption = new MakeModel();
    this.anyMakeOption.id = null;
    this.anyMakeOption.displayName = 'Any';
    this.anyMakeOption.active = true;
    this.anyModelOption = new VehicleModel();
    this.anyModelOption.id = null;
    this.anyModelOption.makeId = null;
    this.anyModelOption.name = 'Any';
    this.anyModelOption.active = true;
  }

  private loadMakes(): void {
    this.vehicleService.getAllMakes().subscribe({
      next: (makes) => {
        this.makes = [this.anyMakeOption, ...makes];
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load makes.'
        });
      }
    });
  }

  private loadModels(): void {
    this.vehicleService.getAllModels().subscribe({
      next: (models) => {
        this.models = models;
        this.filteredModels = [this.anyModelOption, ...models];
        this.applyMakeAndModelSelectionFromForm();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load models.'
        });
      }
    });
  }

  private setDefaultFormValues(): void {
    this.selectedRuleId = null;
    this.ruleForm.setValue({
      ruleType: this.selectedRuleType,
      status: true,
      offerSetId: null,
      year: null,
      make: '',
      makeId: null,
      model: '',
      modelId: null,
      trim: '',
      maxSalePrice: null,
      vehicleLimit: null,
      vehicleCondition: 'New',
      dollarAmountDiscount: null
    });
    this.selectedMakeId = null;
    this.filterModelsForMake(null);
  }

  private setFormForEdit(rule: OfferRuleModel): void {
    this.selectedRuleId = null;
    if (rule.id !== undefined && rule.id !== null) {
      this.selectedRuleId = rule.id;
    }
    const formValues = this.buildEditFormValues(rule);
    this.ruleForm.setValue(formValues);
    this.applyMakeAndModelSelectionFromForm();
  }

  private buildEditFormValues(rule: OfferRuleModel): Record<string, unknown> {
    const ruleType = this.getRuleTypeFromRule(rule);
    const conditionValue = this.normalizeConditionValue(rule.vehicleCondition);
    const offerSetValue = this.resolveRuleNumber(rule.offerSetId);
    const yearValue = this.resolveRuleNumber(rule.year);
    const maxSalePriceValue = this.resolveRuleNumber(rule.maxSalePrice);
    const vehicleLimitValue = this.resolveRuleNumber(rule.vehicleLimit);
    const discountValue = this.resolveRuleNumber(rule.dollarAmountDiscount);
    const makeValue = this.resolveRuleString(rule.make);
    const modelValue = this.resolveRuleString(rule.model);
    const trimValue = this.resolveRuleString(rule.trim);
    const makeIdValue = this.findMakeIdByName(makeValue);
    let modelIdValue: number | null = null;
    if (makeIdValue !== null) {
      modelIdValue = this.findModelIdByNameAndMake(modelValue, makeIdValue);
    }
    const statusValue = this.resolveStatusValue(rule.status);
    return {
      ruleType,
      status: statusValue,
      offerSetId: offerSetValue,
      year: yearValue,
      make: makeValue,
      makeId: makeIdValue,
      model: modelValue,
      modelId: modelIdValue,
      trim: trimValue,
      maxSalePrice: maxSalePriceValue,
      vehicleLimit: vehicleLimitValue,
      vehicleCondition: conditionValue,
      dollarAmountDiscount: discountValue
    };
  }

  private isFormValidForSave(): boolean {
    let isValid = true;
    if (!this.ruleForm.valid) {
      isValid = false;
    }
    const discountRule = this.isDiscountRule();
    const discountControl = this.ruleForm.get('dollarAmountDiscount');
    if (discountRule && discountControl) {
      const discountValue = discountControl.value;
      const hasDiscount = discountValue !== null && discountValue !== undefined && discountValue !== '';
      if (!hasDiscount) {
        isValid = false;
      }
      if (hasDiscount && discountValue <= 0) {
        isValid = false;
      }
    }
    const offerSetControl = this.ruleForm.get('offerSetId');
    if (!discountRule && offerSetControl) {
      const offerSetValue = offerSetControl.value;
      const hasOfferSet = offerSetValue !== null && offerSetValue !== undefined && offerSetValue !== '';
      if (!hasOfferSet) {
        isValid = false;
      }
    }
    return isValid;
  }

  private buildPayloadFromForm(): OfferRuleModel {
    const payload = new OfferRuleModel();
    payload.accountId = this.accountId;
    payload.offerSetId = this.assignNumberFromControl('offerSetId');
    payload.year = this.assignNumberFromControl('year') ?? null;
    payload.maxSalePrice = this.assignNumberFromControl('maxSalePrice') ?? null;
    payload.vehicleLimit = this.assignNumberFromControl('vehicleLimit') ?? null;
    payload.dollarAmountDiscount = this.assignNumberFromControl('dollarAmountDiscount');
    payload.make = this.assignStringFromControl('make');
    payload.model = this.assignStringFromControl('model');
    payload.trim = this.assignStringFromControl('trim');
    payload.vehicleCondition = this.assignCondition();
    payload.status = this.resolveStatusForPayload();
    if (this.selectedRuleId !== null) {
      payload.id = this.selectedRuleId;
    }
    return payload;
  }

  private assignCondition(): string | undefined {
    const conditionControl = this.ruleForm.get('vehicleCondition');
    let condition: string | undefined;
    if (conditionControl) {
      const value = conditionControl.value;
      if (value) {
        condition = value;
      }
    }
    return condition;
  }

  private assignNumberFromControl(controlName: string): number | undefined {
    let numberValue: number | undefined;
    const control = this.ruleForm.get(controlName);
    if (control) {
      const rawValue = control.value;
      const hasValue = rawValue !== null && rawValue !== undefined && rawValue !== '';
      if (hasValue) {
        const parsedValue = Number(rawValue);
        const isValidNumber = !Number.isNaN(parsedValue);
        const isPristineZero = parsedValue === 0 && control.pristine;
        if (isValidNumber && !isPristineZero) {
          numberValue = parsedValue;
        }
      }
    }
    return numberValue;
  }

  private assignStringFromControl(controlName: string): string | undefined {
    let stringValue: string | undefined;
    const control = this.ruleForm.get(controlName);
    if (control) {
      const rawValue = control.value;
      if (rawValue) {
        stringValue = rawValue;
      }
    }
    return stringValue;
  }

  private resolveStatusForPayload(): string | undefined {
    let statusValue: string | undefined;
    const statusControl = this.ruleForm.get('status');
    if (statusControl) {
      const controlValue = statusControl.value;
      let isActive = false;
      if (controlValue === true) {
        isActive = true;
      }
      if (controlValue === 'Active') {
        isActive = true;
      }
      if (isActive) {
        statusValue = 'Active';
      } else {
        statusValue = 'Inactive';
      }
    }
    return statusValue;
  }

  private resolveStatusValue(status: string | boolean | undefined): boolean {
    let isActive = false;
    if (status === true) {
      isActive = true;
    }
    if (status === 'Active') {
      isActive = true;
    }
    return isActive;
  }

  private resolveRuleNumber(value: number | undefined): number | null {
    let resolvedValue: number | null = null;
    if (value !== null && value !== undefined) {
      resolvedValue = value;
    }
    return resolvedValue;
  }

  private resolveRuleString(value: string | undefined): string {
    let resolvedValue = '';
    if (value) {
      resolvedValue = value;
    }
    return resolvedValue;
  }

  private submitRule(payload: OfferRuleModel): void {
    const isUpdate = this.selectedRuleId !== null;
    this.savingRule = true;
    if (isUpdate && this.selectedRuleId !== null) {
      this.offerRuleService.updateOfferRule(this.selectedRuleId, payload).subscribe({
        next: () => {
          this.handleSaveSuccess('Rule updated.');
        },
        error: () => {
          this.handleSaveError('Failed to update rule.');
        }
      });
    } else {
      this.offerRuleService.createOfferRule(payload).subscribe({
        next: () => {
          this.handleSaveSuccess('Rule created.');
        },
        error: () => {
          this.handleSaveError('Failed to create rule.');
        }
      });
    }
  }

  private handleSaveSuccess(detail: string): void {
    this.savingRule = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail
    });
    this.saved.emit();
  }

  private handleSaveError(detail: string): void {
    this.savingRule = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail
    });
  }

  private resetState(): void {
    this.selectedRuleId = null;
    this.savingRule = false;
  }

  isDiscountRule(): boolean {
    let isDiscount = false;
    const ruleType = this.ruleForm.get('ruleType');
    if (ruleType) {
      isDiscount = ruleType.value === 'discountRule';
    }
    return isDiscount;
  }

  private buildOfferSetPatch(offerSet: EligibleOfferSetModel): Record<string, unknown> {
    const maxSalePrice = this.resolveMaxSalePrice(offerSet);
    let conditionValue = 'Any';
    if (offerSet.vehicleCondition) {
      conditionValue = offerSet.vehicleCondition;
    }
    const offerSetMake = this.resolveRuleString(offerSet.make);
    const offerSetModel = this.resolveRuleString(offerSet.model);
    const offerSetMakeId = this.findMakeIdByName(offerSetMake);
    let offerSetModelId: number | null = null;
    if (offerSetMakeId !== null) {
      offerSetModelId = this.findModelIdByNameAndMake(offerSetModel, offerSetMakeId);
    }
    const patch = {
      year: offerSet.year,
      make: offerSetMake,
      makeId: offerSetMakeId,
      model: offerSetModel,
      modelId: offerSetModelId,
      trim: this.resolveRuleString(offerSet.trim),
      maxSalePrice,
      vehicleCondition: conditionValue
    };
    return patch;
  }

  private resolveMaxSalePrice(offerSet: EligibleOfferSetModel): number | null {
    let maxSalePrice = null;
    if (offerSet.suggestedMaxSalePrice !== null && offerSet.suggestedMaxSalePrice !== undefined) {
      maxSalePrice = offerSet.suggestedMaxSalePrice;
    }
    if (maxSalePrice === null && offerSet.msrp !== null && offerSet.msrp !== undefined) {
      maxSalePrice = offerSet.msrp;
    }
    if (maxSalePrice === null && offerSet.buyPrice !== null && offerSet.buyPrice !== undefined) {
      maxSalePrice = offerSet.buyPrice;
    }
    return maxSalePrice;
  }

  private filterModelsForMake(makeId: number | null): void {
    this.selectedMakeId = makeId;
    this.modelDropdownDisabled = true;
    this.filteredModels = [this.anyModelOption];
    if (makeId !== null) {
      this.filteredModels = [this.anyModelOption, ...this.models.filter((model) => model.makeId === makeId)];
      this.modelDropdownDisabled = false;
    }
    const modelIdControl = this.ruleForm.get('modelId');
    const modelControl = this.ruleForm.get('model');
    if (modelIdControl && modelControl) {
      const selectedModelId = modelIdControl.value;
      const modelStillAvailable = this.filteredModels.some((model) => model.id === selectedModelId);
      if (!modelStillAvailable) {
        modelIdControl.setValue(null);
        modelControl.setValue('');
      }
    }
  }

  private applyMakeAndModelSelectionFromForm(): void {
    const makeName = this.getControlStringValue('make');
    const makeId = this.findMakeIdByName(makeName);
    this.ruleForm.patchValue({
      makeId
    });
    this.filterModelsForMake(makeId);
    const modelName = this.getControlStringValue('model');
    let modelId = this.findModelIdByNameAndMake(modelName, makeId);
    let resolvedModel = modelName;
    if (modelId === null) {
      resolvedModel = '';
    }
    this.ruleForm.patchValue({
      model: resolvedModel,
      modelId
    });
  }

  private getControlStringValue(controlName: string): string {
    let value = '';
    const control = this.ruleForm.get(controlName);
    if (control) {
      const controlValue = control.value;
      if (controlValue) {
        value = controlValue;
      }
    }
    return value;
  }

  private findMakeIdByName(makeName: string): number | null {
    let makeId: number | null = null;
    if (makeName) {
      this.makes.forEach((make) => {
        if (makeId === null) {
          if (make.displayName === makeName) {
            makeId = make.id as number | null;
          }
        }
      });
    }
    return makeId;
  }

  private findModelIdByNameAndMake(modelName: string, makeId: number | null): number | null {
    let modelId: number | null = null;
    if (modelName) {
      this.models.forEach((model) => {
        if (modelId === null) {
          const hasMakeMatch = makeId === null || model.makeId === makeId;
          if (hasMakeMatch) {
            if (model.name === modelName) {
              modelId = model.id;
            }
          }
        }
      });
    }
    return modelId;
  }

  private getMakeNameById(makeId: number | null): string {
    let makeName = '';
    const hasValue = makeId !== null;
    if (hasValue) {
      const match = this.makes.find((make) => make.id === makeId);
      if (match) {
        makeName = match.displayName;
      }
    }
    return makeName;
  }

  private getRuleTypeFromRule(rule: OfferRuleModel): string {
    let ruleType = 'offerSetRule';
    const hasOfferSet = rule.offerSetId !== null && rule.offerSetId !== undefined;
    if (!hasOfferSet) {
      ruleType = 'discountRule';
    }
    return ruleType;
  }

  private normalizeConditionValue(conditionValue: string | undefined): string {
    let normalized = 'Any';
    if (conditionValue) {
      const upperValue = conditionValue.toUpperCase();
      if (upperValue === 'NEW') {
        normalized = 'New';
      }
      if (upperValue === 'USED') {
        normalized = 'Used';
      }
      if (upperValue === 'CPO') {
        normalized = 'CPO';
      }
      if (upperValue === 'CERTIFIED') {
        normalized = 'CPO';
      }
      if (upperValue === 'ANY') {
        normalized = 'Any';
      }
    }
    return normalized;
  }
}
